/**
 * Betty Live – kompakte Kachel (Noble Slate).
 * Spiegel von PEBase LucyLiveOrb — State-Namen speaking_lucy historisch belassen.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mic, MicOff, PhoneOff, X } from 'lucide-react';
import {
  getLiveAudioService,
  type LiveAudioState,
} from '../../services/liveAudioService';

const FAB_BASE =
  'w-11 h-11 rounded-full bg-[#15171e] border border-[#E6C299]/30 shadow-[0_0_20px_rgba(230,194,153,0.15)] flex items-center justify-center text-[#E6C299] transition-all focus:outline-none relative';

/** Desktop: exakt gleiche Breite/Höhe wie Zoom/Scroll (7.5rem × h-10) */
const PILL_BASE =
  'w-full h-10 inline-flex items-center justify-center gap-1 bg-[#15171e] hover:bg-[#1a1d24] text-[#E6C299] border border-[#E6C299]/30 shadow-[0_0_15px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(230,194,153,0.2)] transition-all px-1.5 rounded-full focus:outline-none';
const PILL_LABEL = 'font-bold uppercase tracking-wide text-[8px] whitespace-nowrap';
const PILL_ICON = 'w-3.5 h-3.5 shrink-0';

type BettyLiveOrbProps = {
  /** Mobil: runder FAB neben Frag Betty (kein breites Tile) */
  compact?: boolean;
  /** Aktueller PEOne-Bereich (Label), analog Frag Betty */
  currentScreenContext?: string;
};

export function BettyLiveOrb({
  compact = false,
  currentScreenContext,
}: BettyLiveOrbProps): React.ReactElement {
  const liveAudio = useMemo(() => getLiveAudioService(), []);
  const [state, setState] = useState<LiveAudioState>('idle');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const offState = liveAudio.onStateChange((next, detail) => {
      setState(next);
      if (next === 'error') setErrorDetail(detail || 'Verbindung fehlgeschlagen');
      else setErrorDetail(null);
    });
    return () => {
      offState();
    };
  }, [liveAudio]);

  const onIdleClick = useCallback(async () => {
    try {
      await liveAudio.startSession({ currentScreenContext });
    } catch {
      /* State kommt über onStateChange('error') */
    }
  }, [liveAudio, currentScreenContext]);

  const onToggleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    liveAudio.mute(next);
  }, [liveAudio, muted]);

  const onHangUp = useCallback(() => {
    liveAudio.stopSession();
    setMuted(false);
    setErrorDetail(null);
  }, [liveAudio]);

  const onErrorReset = useCallback(() => {
    liveAudio.stopSession();
    setErrorDetail(null);
  }, [liveAudio]);

  if (compact) {
    if (state === 'idle') {
      return (
        <button
          type="button"
          title="Betty Live"
          aria-label="Mit Betty Live sprechen"
          onClick={() => void onIdleClick()}
          className={`${FAB_BASE} hover:border-[#E6C299]/70`}
        >
          <Mic className="w-5 h-5" strokeWidth={2} />
        </button>
      );
    }
    if (state === 'connecting') {
      return (
        <div className={`${FAB_BASE} border-[#E6C299]/50`} role="status" aria-live="polite">
          <span
            className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#E6C299] animate-spin"
            aria-hidden
          />
          <Mic className="w-5 h-5 opacity-70" strokeWidth={2} />
        </div>
      );
    }
    if (state === 'error') {
      return (
        <button
          type="button"
          onClick={onErrorReset}
          title={errorDetail || 'Fehler – tippen zum Schließen'}
          className={`${FAB_BASE} border-red-500/50 text-red-400`}
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>
      );
    }
    return (
      <div
        className="flex items-center gap-1.5 bg-[#1E293B]/95 border border-[#E6C299]/50 rounded-full pl-1.5 pr-1.5 py-1 shadow-lg"
        role="region"
        aria-label="Betty Live Gespräch"
      >
        <div className={`${FAB_BASE} w-9 h-9 border-[#E6C299]/50`}>
          <span className="pointer-events-none absolute inset-0 rounded-full bg-red-500/20 animate-pulse" aria-hidden />
          <Mic className="relative w-4 h-4" strokeWidth={2} />
        </div>
        <button
          type="button"
          onClick={onToggleMute}
          title={muted ? 'Mikrofon an' : 'Stumm'}
          aria-label={muted ? 'Mikrofon an' : 'Stumm schalten'}
          className={`w-8 h-8 rounded-full flex items-center justify-center border ${
            muted
              ? 'bg-red-500/15 border-red-500/40 text-red-400'
              : 'bg-[#0F172A] border-slate-600 text-slate-300'
          }`}
        >
          {muted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={onHangUp}
          title="Gespräch beenden"
          aria-label="Gespräch beenden"
          className="w-8 h-8 rounded-full flex items-center justify-center bg-red-600/90 text-white border border-red-400/40"
        >
          <PhoneOff className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  if (state === 'idle') {
    return (
      <button
        type="button"
        title="Mit Betty Live sprechen"
        aria-label="Mit Betty Live sprechen"
        onClick={() => void onIdleClick()}
        className={PILL_BASE}
      >
        <Mic className={`${PILL_ICON} group-hover:scale-110 transition-transform`} strokeWidth={2} />
        <span className={PILL_LABEL}>Betty Live</span>
      </button>
    );
  }

  if (state === 'connecting') {
    return (
      <div className={`${PILL_BASE} cursor-default`} role="status" aria-live="polite">
        <span className={`relative ${PILL_ICON}`} aria-hidden>
          <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#E6C299] animate-spin" />
          <Mic className={`${PILL_ICON} opacity-70`} strokeWidth={2} />
        </span>
        <span className={PILL_LABEL}>Verbindet</span>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <button
        type="button"
        onClick={onErrorReset}
        title={errorDetail || 'Fehler – tippen zum Schließen'}
        className={`${PILL_BASE} border-red-500/40 text-red-400 hover:bg-red-500/10`}
      >
        <X className={PILL_ICON} strokeWidth={2} />
        <span className={PILL_LABEL}>Fehler</span>
      </button>
    );
  }

  return (
    <div
      className="w-full h-10 inline-flex items-center justify-center gap-1 bg-[#15171e] border border-[#E6C299]/30 shadow-[0_0_15px_rgba(0,0,0,0.5)] pl-1.5 pr-1 rounded-full"
      role="region"
      aria-label="Betty Live Gespräch"
    >
      <span className="relative w-7 h-7 rounded-full flex items-center justify-center text-[#E6C299] shrink-0">
        <span className="pointer-events-none absolute inset-0 rounded-full bg-red-500/20 animate-pulse" aria-hidden />
        <Mic className="relative w-3.5 h-3.5" strokeWidth={2} />
      </span>
      <span className={`${PILL_LABEL} text-[#E6C299] mr-auto`}>Live</span>
      <button
        type="button"
        onClick={onToggleMute}
        title={muted ? 'Mikrofon an' : 'Stumm'}
        aria-label={muted ? 'Mikrofon an' : 'Stumm schalten'}
        className={`w-7 h-7 rounded-full flex items-center justify-center border ${
          muted
            ? 'bg-red-500/15 border-red-500/40 text-red-400'
            : 'bg-[#08090b] border-slate-600 text-slate-300 hover:text-[#E6C299] hover:border-[#E6C299]/50'
        }`}
      >
        {muted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        onClick={onHangUp}
        title="Gespräch beenden"
        aria-label="Gespräch beenden"
        className="w-7 h-7 rounded-full flex items-center justify-center bg-red-600/90 hover:bg-red-500 text-white border border-red-400/40"
      >
        <PhoneOff className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default BettyLiveOrb;
